import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import * as authAPI from './auth';
import { debugLog } from './debug';

const AuthContext = createContext(null);

// Dev bypass for local development without vercel dev
const DEV_BYPASS = process.env.REACT_APP_BYPASS_AUTH === 'true';
debugLog('[Auth] DEV_BYPASS:', DEV_BYPASS, '(REACT_APP_BYPASS_AUTH:', process.env.REACT_APP_BYPASS_AUTH, ')');

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_BYPASS);
  const [isLoading, setIsLoading] = useState(!DEV_BYPASS);
  const authRequestVersionRef = useRef(0);

  // Verify token on initial load
  useEffect(() => {
    if (DEV_BYPASS) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const verifyStoredToken = async () => {
      const requestVersion = ++authRequestVersionRef.current;
      const token = authAPI.getToken();

      if (!token) {
        if (isMounted && requestVersion === authRequestVersionRef.current) {
          setIsAuthenticated(false);
          setIsLoading(false);
        }
        return;
      }

      const result = await authAPI.verifyToken(token);

      // Ignore stale verification responses when a newer login/logout happened.
      if (!isMounted || requestVersion !== authRequestVersionRef.current) {
        return;
      }

      setIsAuthenticated(result.valid);
      if (!result.valid && authAPI.getToken() === token) {
        authAPI.clearToken();
      }

      setIsLoading(false);
    };

    verifyStoredToken();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (username, password) => {
    if (DEV_BYPASS) {
      debugLog('[DEV] Auth bypassed - auto-login');
      setIsAuthenticated(true);
      return true;
    }

    const requestVersion = ++authRequestVersionRef.current;
    setIsLoading(true);

    try {
      const { token } = await authAPI.login(username, password);

      // Ignore stale login responses if a newer auth action happened.
      if (requestVersion !== authRequestVersionRef.current) {
        return false;
      }

      authAPI.storeToken(token);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      if (requestVersion === authRequestVersionRef.current) {
        setIsAuthenticated(false);
      }
      console.error('Login failed:', error);
      return false;
    } finally {
      if (requestVersion === authRequestVersionRef.current) {
        setIsLoading(false);
      }
    }
  };

  const logout = () => {
    authRequestVersionRef.current += 1;
    authAPI.clearToken();
    setIsAuthenticated(false);
    setIsLoading(false);
  };

  // Provide the authentication context
  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
